const AXIOS_STATUS_TEXT = /^request failed with status code \d+$/i;

/** First useful string in a PGW/WMS error body, including nested `{ error: { message } }`. */
export function readApiErrorText(error) {
  const data = error?.response?.data;
  const stack = [data, error?.message];
  const seen = new Set();
  let axiosFallback = "";

  while (stack.length) {
    const value = stack.shift();
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) continue;
      if (AXIOS_STATUS_TEXT.test(trimmed)) {
        axiosFallback = trimmed;
        continue;
      }
      return trimmed;
    }
    if (!value || typeof value !== "object" || seen.has(value)) continue;
    seen.add(value);
    stack.push(value.error, value.message, value.reason);
  }

  return axiosFallback;
}

export function expirationPatchErrorMessage(error) {
  const raw = readApiErrorText(error);
  const lower = raw.toLowerCase();
  if (lower.includes("insufficientavailability")) {
    return "לא ניתן לשנות תוקף: אין מספיק זמינות בקטלוג לכיסוי השינוי.";
  }
  if (lower.includes("toomanyspots")) {
    return "לא ניתן לשנות תוקף: יותר מדי תאים משויכים למנה.";
  }
  if (/(^|[^a-z])allocated([^a-z]|$)/.test(lower)) {
    return "לא ניתן לשנות תוקף: יש מלאי מוקצה מהמנה.";
  }
  if (error?.response?.status === 409 || error?.response?.status === 503) {
    return "לא ניתן לשנות תוקף כרגע (התנגשות). נסו שוב.";
  }
  return raw || "שגיאה בעדכון התוקף";
}

export function catalogQuantityOf(inventory) {
  if (!inventory) return 0;
  const record = inventory.availabilityRecord;
  if (record == null || Number.isNaN(Number(record))) {
    const available = Number(inventory.availableInventory ?? 0);
    return Number.isFinite(available) ? available : 0;
  }
  return Number(record);
}

export function rebuildPlanCounts(summary) {
  const toWrite = Number(summary?.toWrite ?? summary?.write ?? 0);
  const inSync = Number(summary?.inSync ?? 0);
  const skipped = Number(summary?.skipped ?? 0);
  return {
    toWrite: Number.isFinite(toWrite) ? toWrite : 0,
    inSync: Number.isFinite(inSync) ? inSync : 0,
    skipped: Number.isFinite(skipped) ? skipped : 0,
  };
}

export function rebuildPlanConfirmText(summary) {
  const { toWrite, inSync, skipped } = rebuildPlanCounts(summary);
  return `לעדכון: ${toWrite}. תואמים: ${inSync}. דילוג: ${skipped}. הפעולה משנה את הכמות שהחנות והעגלה רואים לכל הסניף.`;
}

/**
 * Catalog rows that were written. Empty when any write conflicted or failed,
 * because the report does not say which product was rejected.
 */
export function catalogUpdatesFromRebuild(result) {
  if (!result?.apply) return [];
  const conflicts = Number(result.summary?.conflicts ?? 0);
  const failed = Number(result.summary?.failed ?? 0);
  if (conflicts > 0 || failed > 0) return [];
  if (!Array.isArray(result.rows)) return [];

  return result.rows
    .filter((row) => row?.action === "write" && row.productId != null)
    .map((row) => ({
      productId: String(row.productId),
      availableQuantity: Number(row.expected),
    }))
    .filter((row) => Number.isFinite(row.availableQuantity));
}

export function rebuildResultAlert(result) {
  const applied = Number(result?.summary?.applied ?? 0);
  const conflicts = Number(result?.summary?.conflicts ?? 0);
  const failed = Number(result?.summary?.failed ?? 0);
  const truncated = Boolean(result?.truncated);
  const partial = conflicts > 0 || failed > 0 || truncated;
  const parts = [`עודכנו ${Number.isFinite(applied) ? applied : 0} רשומות`];
  if (conflicts > 0) parts.push(`${conflicts} התנגשויות`);
  if (failed > 0) parts.push(`${failed} כשלונות`);
  if (truncated) parts.push("הדוח מקוצר");
  if (partial) parts.push("רעננו את העמוד כדי לראות את שאר המוצרים");
  return {
    partial,
    msgType: partial ? "warning" : "success",
    msgTitle: partial ? "העדכון הושלם חלקית" : "זמינות הקטלוג עודכנה",
    msgText: `${parts.join(". ")}.`,
  };
}

export function rebuildRequestErrorMessage(error) {
  const status = error?.response?.status;
  if (
    !error?.response ||
    status === 500 ||
    status === 502 ||
    status === 503 ||
    status === 504
  ) {
    return "ייתכן שחלק מהמוצרים עודכנו לפני שהפעולה נקטעה. בדקו את הזמינות לפני ניסיון נוסף.";
  }
  return readApiErrorText(error) || "שגיאה בעדכון זמינות הקטלוג";
}
